import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * InventoryOverview — Inventory dashboard page
 *
 * Route: /inventory  (or /inventory/overview)
 *
 * Pulls data from all 6 modules in parallel at mount.
 * Every stat uses per_page=1 — only the pagination total is needed,
 * not the actual rows. Cheap and fast.
 *
 * Sections:
 *   1. Top KPI row     — 6 headline numbers
 *   2. Asset breakdown — status + ownership distribution (mini bar charts)
 *   3. Active alerts   — open RMAs · expiring warranties · active leases
 *   4. Recent activity — last 5 transfers + last 5 RMA cases interleaved
 *   5. Quick links     — navigate to each module list page
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Alert,
  Skeleton,
  Stack,
  Grid,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  LinearProgress,
  Tooltip,
  Divider,
  CardContent,
  TableContainer,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Icons
import InventoryIcon from "@mui/icons-material/Inventory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon from "@mui/icons-material/Link";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorageIcon from "@mui/icons-material/Storage";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import { useAuth } from "../../../../auth/useAuth";
import { Insights } from "@mui/icons-material";
import { can } from "../../../../auth/can";

const API = `${APP_INVENTORY_API_BASE}`;

// ── Helpers ───────────────────────────────────────────────────────────────────

// Days between today and a date string
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// Cheap count fetch — only reads meta.pagination.total_items
async function fetchCount(url, params, headers) {
  try {
    const res = await axios.get(url, {
      headers,
      params: { per_page: 1, page: 1, ...params },
    });
    return res.data?.meta?.pagination?.total_items ?? 0;
  } catch {
    return 0;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function KpiCard({
  title,
  value,
  icon,
  bg,
  color,
  subtitle,
  loading,
  onClick,
}) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        p: 2.5,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.3s ease",
        backgroundColor: "rgba(255,255,255,0.08)",
        color: "#F8FAFC",

        "&:hover": onClick
          ? {
            transform: "translateY(-6px) scale(1.02)",
            boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
          }
          : {},
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box>
          <Typography
            variant="body2"
            color="rgba(226,232,240,0.72)"
            fontWeight={500}
            mb={0.5}
          >
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={60} height={40} />
          ) : (
            <Typography variant="h4" fontWeight={700}>
              {value?.toLocaleString() ?? "—"}
            </Typography>
          )}
          {subtitle && !loading && (
            <Typography variant="caption" color="rgba(226,232,240,0.72)">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 10,
            backdropFilter: "blur(10px)",
            background: "rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "all 0.3s ease",

            ".MuiCard-root:hover &": {
              transform: "scale(1.1)",
            },
          }}
        >
          {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
        </Box>
      </Box>
    </Card>
  );
}

function MiniBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box mb={1.5}>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" fontWeight={600}>
          {value.toLocaleString()}{" "}
          <span style={{ color: "#9ca3af" }}>({pct}%)</span>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: "#f3f4f6",
          "& .MuiLinearProgress-bar": {
            backgroundColor: color,
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
}

function SectionHeader({
  title,
  linkTo,
  linkLabel,
  navigate,
  sx = {},
  titleSx = {},
  buttonSx = {},
}) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
      sx={sx}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={titleSx}>
        {title}
      </Typography>
      {linkTo && (
        <Button
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(linkTo)}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 600,
            ...buttonSx,
          }}
        >
          {linkLabel}
        </Button>
      )}
    </Box>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function InventoryOverview() {
  const navigate = useNavigate();
  const { activeOrg, permissions } = useAuth();
  const token = localStorage.getItem("authToken");
  const isPlatformOwner =
    permissions?.has?.("*") || can(permissions, "system.manage");
  const hasPermission = (permission) => can(permissions, permission);
  const orgType = (activeOrg?.type || "").toUpperCase();
  const isDistributor = orgType === "DISTRIBUTOR";
  const isReseller = orgType === "RESELLER";
  const isCustomer = orgType === "CUSTOMER";

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  // ── State ──
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({});
  const [assetBreakdown, setAssetBreakdown] = useState({});
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [recentRma, setRecentRma] = useState([]);
  const [expiringAssets, setExpiringAssets] = useState([]);
  const [openRma, setOpenRma] = useState([]);
  const [activeLeases, setActiveLeases] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);

  // ── Fetch KPIs — all parallel, cheap count calls ──
  const fetchKpis = useCallback(async () => {
    setLoading(true);
    const [
      totalAssets,
      inStock,
      deployed,
      rmaReturned,
      totalTransfers,
      activeBindings,
      activeLeaseCount,
      openRmaCount,
      inProgressRma,
    ] = await Promise.all([
      fetchCount(`${API}/assets`, {}, authHeaders),
      fetchCount(`${API}/assets`, { status: "IN_STOCK" }, authHeaders),
      fetchCount(`${API}/assets`, { status: "DEPLOYED" }, authHeaders),
      fetchCount(
        `${API}/assets`,
        { ownership_state: "RMA_RETURNED" },
        authHeaders,
      ),
      hasPermission("inventory.transfer.view")
        ? fetchCount(`${API}/transfers`, {}, authHeaders)
        : 0,
      hasPermission("inventory.binding.view")
        ? fetchCount(`${API}/bindings`, { active: "true" }, authHeaders)
        : 0,
      hasPermission("inventory.lease.view")
        ? fetchCount(`${API}/leases`, { lease_status: "ACTIVE" }, authHeaders)
        : 0,
      hasPermission("inventory.rma.view")
        ? fetchCount(`${API}/rma`, { case_status: "OPEN" }, authHeaders)
        : 0,
      hasPermission("inventory.rma.view")
        ? fetchCount(`${API}/rma`, { case_status: "IN_PROGRESS" }, authHeaders)
        : 0,
    ]);

    setKpis({
      totalAssets,
      inStock,
      deployed,
      rmaReturned,
      totalTransfers,
      activeBindings,
      activeLeaseCount,
      openRmaCount: openRmaCount + inProgressRma,
    });

    // Asset ownership breakdown
    const [oemOwned, partner, customer] = await Promise.all([
      fetchCount(
        `${API}/assets`,
        { ownership_state: "OEM_OWNED" },
        authHeaders,
      ),
      fetchCount(
        `${API}/assets`,
        { ownership_state: "OWNED_BY_PARTNER" },
        authHeaders,
      ),
      fetchCount(
        `${API}/assets`,
        { ownership_state: "OWNED_BY_CUSTOMER" },
        authHeaders,
      ),
    ]);
    setAssetBreakdown({ oemOwned, partner, customer, total: totalAssets });

    setLoading(false);
  }, [activeOrg, permissions, token]);

  // ── Fetch alerts (expiring warranties, open RMAs, active leases) ──
  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const [rmaRes, leaseRes] = await Promise.all([
        hasPermission("inventory.rma.view")
          ? axios.get(`${API}/rma`, {
            headers: authHeaders,
            params: { case_status: "OPEN", per_page: 5 },
          })
          : Promise.resolve({ data: { data: [] } }),
        hasPermission("inventory.lease.view")
          ? axios.get(`${API}/leases`, {
            headers: authHeaders,
            params: { lease_status: "ACTIVE", per_page: 5 },
          })
          : Promise.resolve({ data: { data: [] } }),
      ]);
      setOpenRma(rmaRes.data.data || []);
      setActiveLeases(leaseRes.data.data || []);

      // Expiring warranties — assets with warranty_end within 90 days
      // We fetch recently updated assets and filter client-side
      // (no warranty_end filter on the API — this is best-effort)
      if (hasPermission("inventory.asset.view")) {
        const assetRes = await axios.get(`${API}/assets`, {
          headers: authHeaders,
          params: {
            per_page: 50,
            sort: "warranty_end",
            order: "asc",
            status: "DEPLOYED",
          },
        });
        const today = new Date();
        const in90Days = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);
        const expiring = (assetRes.data.data || [])
          .filter((a) => {
            if (!a.warranty_end) return false;
            const end = new Date(a.warranty_end);
            return end >= today && end <= in90Days;
          })
          .slice(0, 5);
        setExpiringAssets(expiring);
      } else {
        setExpiringAssets([]);
      }
    } catch {
      /* silently fail */
    }
    setAlertsLoading(false);
  }, [activeOrg, permissions, token]);

  // ── Fetch recent activity ──
  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const [transferRes, rmaRes] = await Promise.all([
        hasPermission("inventory.transfer.view")
          ? axios.get(`${API}/transfers`, {
            headers: authHeaders,
            params: { per_page: 5 },
          })
          : Promise.resolve({ data: { data: [] } }),
        hasPermission("inventory.rma.view")
          ? axios.get(`${API}/rma`, {
            headers: authHeaders,
            params: { per_page: 5 },
          })
          : Promise.resolve({ data: { data: [] } }),
      ]);
      setRecentTransfers(transferRes.data.data || []);
      setRecentRma(rmaRes.data.data || []);
    } catch {
      /* silently fail */
    }
    setActivityLoading(false);
  }, [activeOrg, permissions, token]);

  useEffect(() => {
    fetchKpis();
    fetchAlerts();
    fetchActivity();
  }, [fetchKpis, fetchAlerts, fetchActivity]);

  const cardStyle = {
    flex: {
      xs: "1 1 calc(100% - 8px)",
      sm: "1 1 calc(50% - 8px)",
      md: "1 1 calc(33.33% - 12px)",
    },
    maxWidth: {
      xs: "calc(100% - 8px)",
      sm: "calc(50% - 8px)",
      md: "calc(33.33% - 12px)",
    },
  };

  // ── Quick links config ──
  const quickLinks = [
    {
      label: "Assets",
      path: "/inventory/assets",
      icon: <InventoryIcon />,
      color: "#1d4ed8",
      bg: "#eff6ff",
      perm: "inventory.asset.view",
    },
    {
      label: "Transfers",
      path: "/inventory/transfers",
      icon: <SwapHorizIcon />,
      color: "#7c3aed",
      bg: "#f5f3ff",
      perm: "inventory.transfer.view",
    },
    {
      label: "Bindings",
      path: "/inventory/bindings",
      icon: <LinkIcon />,
      color: "#065f46",
      bg: "#ecfdf5",
      perm: "inventory.binding.view",
    },
    {
      label: "Movements",
      path: "/inventory/movements",
      icon: <LocalShippingIcon />,
      color: "#92400e",
      bg: "#fff7ed",
      perm: "inventory.movement.view",
    },
    {
      label: "Leases",
      path: "/inventory/leases",
      icon: <HandshakeIcon />,
      color: "#0e7490",
      bg: "#ecfeff",
      perm: "inventory.lease.view",
    },
    {
      label: "RMA Cases",
      path: "/inventory/rma",
      icon: <ReportProblemIcon />,
      color: "#dc2626",
      bg: "#fff5f5",
      perm: "inventory.rma.view",
    },
    {
      label: "Locations",
      path: "/inventory/locations",
      icon: <StorageIcon />,
      color: "#374151",
      bg: "#f3f4f6",
      perm: "inventory.location.view",
    },
  ];
  const visibleQuickLinks = quickLinks.filter((l) => hasPermission(l.perm));

  const transferTypeColors = {
    OEM_SALE: { color: "#1d4ed8", bg: "#eff6ff" },
    DISTRIBUTOR_SALE: { color: "#7c3aed", bg: "#f5f3ff" },
    RESELLER_SALE: { color: "#065f46", bg: "#ecfdf5" },
    RMA_RETURN: { color: "#92400e", bg: "#fff7ed" },
  };

  const rmaStatusColors = {
    OPEN: { color: "#92400e", bg: "#fff7ed" },
    IN_PROGRESS: { color: "#1d4ed8", bg: "#eff6ff" },
    RESOLVED: { color: "#065f46", bg: "#ecfdf5" },
    CLOSED: { color: "#374151", bg: "#f3f4f6" },
    CANCELLED: { color: "#dc2626", bg: "#fff5f5" },
  };

  const workspaceLabel = isPlatformOwner
    ? "Platform Command"
    : isDistributor
      ? "Distributor Workspace"
      : isReseller
        ? "Reseller Workspace"
        : isCustomer
          ? "Customer Workspace"
          : "Organization Workspace";
  const workspaceSummary = isPlatformOwner
    ? "Global inventory command view across every operational module."
    : isDistributor
      ? "Receive stock from CoreData, track custody, and sell onward to resellers or customers."
      : isReseller
        ? "Track received stock, maintain final sale records, and keep provisioning-ready hardware visible."
        : isCustomer
          ? "Monitor owned inventory, bind eligible assets to devices, and track service exceptions."
          : "Your organization inventory workspace for custody, resale, and provisioning-ready stock.";

  return (
    <Box
      sx={{
        // maxWidth: "1400px",
        margin: "0 auto",
        p: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
      }}
    >
      {/* ── Header ── */}

      <Box mb={4}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            p: 2.5,
            mb: 3,
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                <Box>
                  {/* <Typography
                    variant="overline"
                    color="text.se"
                    fontWeight={700}
                  >
                    {workspaceLabel}
                  </Typography> */}
                  <Typography variant="h4" fontWeight={700} color="#fff"sx={{fontSize:{xs:28,md:40} }}>
                    Inventory Overview
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="#bdbcbc">
                {workspaceSummary}
              </Typography>
            </Box>
            {!isPlatformOwner && (
              <Stack spacing={0.75}>
                <Typography variant="body2" color="text.secondary">
                  {isDistributor
                    ? "Use transfers to receive OEM stock and sell onward."
                    : isReseller
                      ? "Use transfers to complete customer handoffs."
                      : "Provision only from in-stock, unbound assets in your workspace."}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isCustomer
                    ? "Track active bindings, ownership, and service exceptions."
                    : "Only custody-relevant inventory actions are surfaced in this view."}
                </Typography>
              </Stack>
            )}
          </Stack>
          {/* ── Section 1: KPI Row ── */}
          <Box
            sx={{
              display: "flex",
              // flexDirection: {xs:"column"},
              flexWrap: "wrap",
              gap: 2,
              mt: 4,
            }}
          >
            {/* CARD 1 */}
            <Box sx={cardStyle}>
              <KpiCard
                title="Total assets"
                value={kpis.totalAssets}
                icon={<InventoryIcon />}
                color="#3b82f6"
                loading={loading}
                onClick={() => navigate("/inventory/assets")}
              />
            </Box>

            {/* CARD 2 */}
            <Box sx={cardStyle}>
              <KpiCard
                title="In stock"
                value={kpis.inStock}
                icon={<StorageIcon />}
                color="#6366f1"
                loading={loading}
                onClick={() => navigate("/inventory/assets?status=IN_STOCK")}
              />
            </Box>

            {/* CARD 3 */}
            <Box sx={cardStyle}>
              <KpiCard
                title="Deployed"
                value={kpis.deployed}
                icon={<CheckCircleIcon />}
                color="#22c55e"
                loading={loading}
                onClick={() => navigate("/inventory/bindings?active=true")}
              />
            </Box>

            {/* CARD 4 */}
            {hasPermission("inventory.lease.view") && (
              <Box sx={cardStyle}>
                <KpiCard
                  title="Active leases"
                  value={kpis.activeLeaseCount}
                  icon={<HandshakeIcon />}
                  color="#14b8a6"
                  loading={loading}
                  onClick={() =>
                    navigate("/inventory/leases?lease_status=ACTIVE")
                  }
                />
              </Box>
            )}

            {/* CARD 5 */}
            {hasPermission("inventory.rma.view") && (
              <Box sx={cardStyle}>
                <KpiCard
                  title="Open RMAs"
                  value={kpis.openRmaCount}
                  icon={<ReportProblemIcon />}
                  color="#ef4444"
                  loading={loading}
                  onClick={() => navigate("/inventory/rma?case_status=OPEN")}
                />
              </Box>
            )}

            {/* CARD 6 */}
            <Box sx={cardStyle}>
              <KpiCard
                title="Total transfers"
                value={kpis.totalTransfers}
                icon={<SwapHorizIcon />}
                color="#8b5cf6"
                loading={loading}
                onClick={() => navigate("/inventory/transfers")}
              />
            </Box>
          </Box>
        </Card>
      </Box>

      {/* ── Section 2: Asset Breakdown + Alerts ── */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
        gap={3}
        mb={4}
      >
        {/* Asset ownership breakdown */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            p: 2.5,
          }}
        >
          <SectionHeader
            title={isPlatformOwner ? "Asset ownership" : "Inventory custody"}
            navigate={navigate}
            linkTo="/inventory/assets"
            linkLabel="View all"
            sx={{
              mx: -2.5,
              mt: -2.5,
              mb: 2.5,
              px: 2.5,
              py: 2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              background:
                 "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
            }}
            titleSx={{ color: "#F8FAFC",fontSize: { xs: 15, md: 20 } }}
            buttonSx={{ color: "#E0F2FE" }}
          />
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Box key={i} mb={1.5}>
                <Skeleton
                  variant="text"
                  width="40%"
                  height={16}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton variant="rounded" width="100%" height={6} />
              </Box>
            ))
          ) : (
            <>
              <MiniBar
                label={isPlatformOwner ? "OEM owned" : "OEM sourced"}
                value={assetBreakdown.oemOwned || 0}
                total={assetBreakdown.total || 1}
                color="#3b82f6"
              />
              <MiniBar
                label={isPlatformOwner ? "Owned by partner" : "Partner owned"}
                value={assetBreakdown.partner || 0}
                total={assetBreakdown.total || 1}
                color="#8b5cf6"
              />
              <MiniBar
                label={isPlatformOwner ? "Owned by customer" : "Customer owned"}
                value={assetBreakdown.customer || 0}
                total={assetBreakdown.total || 1}
                color="#22c55e"
              />
              <MiniBar
                label="RMA returned"
                value={kpis.rmaReturned || 0}
                total={assetBreakdown.total || 1}
                color="#f59e0b"
              />
              <Divider sx={{ my: 1.5 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Deployed (active bindings)
                </Typography>
                <Typography variant="caption" fontWeight={700} color="#065f46">
                  {kpis.deployed?.toLocaleString() ?? 0}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  In stock
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {kpis.inStock?.toLocaleString() ?? 0}
                </Typography>
              </Box>
            </>
          )}
        </Card>

        {/* Alerts panel */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            p: 2.5,
          }}
        >
          <SectionHeader title="Alerts" navigate={navigate}
            sx={{
                
              mx: -2.5,
              mt: -2.5,
              mb: 2.5,
              px: 2.5,
              py: 2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              background:
               "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
            }}
            titleSx={{ color: "#F8FAFC",fontSize: { xs: 15, md: 20 } }}
            buttonSx={{ color: "#E0F2FE" }}
          />
          {alertsLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={36}
                sx={{ mb: 1, borderRadius: 2 }}
              />
            ))
          ) : (
            <Stack spacing={1}>
              {/* Open RMAs */}
              {openRma.length > 0 ? (
                openRma.slice(0, 3).map((c) => (
                  <Box
                    key={c.id}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    sx={{
                      p: 1.2,
                      bgcolor: "#fff5f5",
                      borderRadius: 2,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#fee2e2" },
                    }}
                    onClick={() =>
                      navigate(`/inventory/assets/${c.inventory_asset_id}`)
                    }
                  >
                    <ReportProblemIcon
                      sx={{ color: "#dc2626", fontSize: 18, flexShrink: 0 }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="#dc2626"
                        noWrap
                      >
                        RMA OPEN —{" "}
                        {c.asset_serial_number ||
                          c.inventory_asset_id?.slice(0, 8)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        noWrap
                      >
                        {c.fault_type?.replace(/_/g, " ")} ·{" "}
                        {c.fault_description?.slice(0, 40)}…
                      </Typography>
                    </Box>
                    <ArrowForwardIcon
                      sx={{ fontSize: 14, color: "#9ca3af", flexShrink: 0 }}
                    />
                  </Box>
                ))
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  sx={{ p: 1.2, bgcolor: "#f0fdf4", borderRadius: 2 }}
                >
                  <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 18 }} />
                  <Typography
                    variant="caption"
                    color="#15803d"
                    fontWeight={600}
                  >
                    No open RMA cases
                  </Typography>
                </Box>
              )}

              {/* Expiring warranties */}
              {expiringAssets.length > 0 ? (
                expiringAssets.slice(0, 2).map((a) => {
                  const days = daysUntil(a.warranty_end);
                  return (
                    <Box
                      key={a.id}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      sx={{
                        p: 1.2,
                        bgcolor: "#fffbeb",
                        borderRadius: 2,
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#fef3c7" },
                      }}
                      onClick={() => navigate(`/inventory/assets/${a.id}`)}
                    >
                      <WarningAmberIcon
                        sx={{ color: "#d97706", fontSize: 18, flexShrink: 0 }}
                      />
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="#92400e"
                          noWrap
                        >
                          WARRANTY EXPIRING — {a.serial_number}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {days <= 0 ? "Expired" : `${days} days remaining`} ·{" "}
                          {a.warranty_end}
                        </Typography>
                      </Box>
                      <ArrowForwardIcon
                        sx={{ fontSize: 14, color: "#9ca3af", flexShrink: 0 }}
                      />
                    </Box>
                  );
                })
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  sx={{ p: 1.2, bgcolor: "#f0fdf4", borderRadius: 2 }}
                >
                  <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 18 }} />
                  <Typography
                    variant="caption"
                    color="#15803d"
                    fontWeight={600}
                  >
                    No warranties expiring in 90 days
                  </Typography>
                </Box>
              )}

              {/* Active leases expiring soon */}
              {activeLeases
                .filter(
                  (l) =>
                    daysUntil(l.end_date) !== null &&
                    daysUntil(l.end_date) <= 30,
                )
                .slice(0, 2)
                .map((l) => (
                  <Box
                    key={l.id}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    sx={{
                      p: 1.2,
                      bgcolor: "#ecfeff",
                      borderRadius: 2,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#cffafe" },
                    }}
                    onClick={() =>
                      navigate(`/inventory/assets/${l.inventory_asset_id}`)
                    }
                  >
                    <HandshakeIcon
                      sx={{ color: "#0e7490", fontSize: 18, flexShrink: 0 }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="#0e7490"
                        noWrap
                      >
                        LEASE ENDING —{" "}
                        {l.asset_serial_number ||
                          l.inventory_asset_id?.slice(0, 8)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {daysUntil(l.end_date)} days · ends {l.end_date}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon
                      sx={{ fontSize: 14, color: "#9ca3af", flexShrink: 0 }}
                    />
                  </Box>
                ))}
            </Stack>
          )}
        </Card>
      </Box>

      {/* ── Section 3: Recent activity ── */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
        gap={3}
        mb={4}
      >
        {/* Recent transfers */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            p: 2.5,
          }}
        >
          <SectionHeader
            title={
              isPlatformOwner
                ? "Recent transfers"
                : "Inbound & outbound handoffs"
            }
            navigate={navigate}
            linkTo="/inventory/transfers"
            linkLabel="View all"
            sx={{
              mx: -2.5,
              mt: -2.5,
              mb: 2.5,
              px: 2.5,
              py: 2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              background:
               "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
            }}
            titleSx={{ color: "#F8FAFC" ,fontSize: { xs: 15, md: 20 } }}
            buttonSx={{ color: "#E0F2FE" }}
          />
          {activityLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={32}
                sx={{ mb: 0.8, borderRadius: 1.5 }}
              />
            ))
          ) : recentTransfers.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              textAlign="center"
              py={3}
            >
              No transfers yet
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      fontSize: 11,
                      color: "text.secondary",
                      bgcolor: "background.default",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    },
                  }}
                >
                  <TableCell>Asset</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentTransfers.map((t) => {
                  const tc = transferTypeColors[t.transfer_type] || {
                    color: "#374151",
                    bg: "#f3f4f6",
                  };
                  return (
                    <TableRow
                      key={t.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/inventory/assets/${t.inventory_asset_id}`)
                      }
                    >
                      <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>
                        {t.asset_serial_number ||
                          t.inventory_asset_id?.slice(0, 8) + "…"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t.transfer_type?.replace(/_/g, " ")}
                          size="small"
                          sx={{
                            bgcolor: tc.bg,
                            color: tc.color,
                            fontWeight: 600,
                            borderRadius: "5px",
                            fontSize: 10,
                            height: 20,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 11,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.transferred_at
                          ? new Date(t.transferred_at).toLocaleDateString(
                            "en-IN",
                            { day: "numeric", month: "short" },
                          )
                          : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Recent RMA cases */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            p: 2.5,
          }}
        >
          <SectionHeader
            title={isPlatformOwner ? "Recent RMA cases" : "Service exceptions"}
            navigate={navigate}
            linkTo="/inventory/rma"
            linkLabel="View all"
            sx={{
              mx: -2.5,
              mt: -2.5,
              mb: 2.5,
              px: 2.5,
              py: 2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
            }}
            titleSx={{ color: "#F8FAFC",fontSize: { xs: 15, md: 20 } }}
            buttonSx={{ color: "#E0F2FE" }}
          />
          {activityLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={32}
                sx={{ mb: 0.8, borderRadius: 1.5 }}
              />
            ))
          ) : recentRma.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              textAlign="center"
              py={3}
            >
              No RMA cases yet
            </Typography>
          ) : (
            <TableContainer
              sx={{
                width: "100%",
                overflowX: { xs: "auto", md: "visible" },
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: { xs: 600, md: "100%" }, 
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        fontWeight: 700,
                        fontSize: 11,
                        color: "text.secondary",
                        bgcolor: "background.default",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      },
                    }}
                  >
                    <TableCell>Asset</TableCell>
                    <TableCell>Fault</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Opened</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {recentRma.map((c) => {
                    const sc = rmaStatusColors[c.case_status] || {
                      color: "#374151",
                      bg: "#f3f4f6",
                    };

                    return (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/inventory/assets/${c.inventory_asset_id}`)
                        }
                      >
                        <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>
                          {c.asset_serial_number ||
                            c.inventory_asset_id?.slice(0, 8) + "…"}
                        </TableCell>

                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                          {c.fault_type?.replace(/_/g, " ")}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={c.case_status?.replace(/_/g, " ")}
                            size="small"
                            sx={{
                              bgcolor: sc.bg,
                              color: sc.color,
                              fontWeight: 600,
                              borderRadius: "5px",
                              fontSize: 10,
                              height: 20,
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            fontSize: 11,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.opened_at
                            ? new Date(c.opened_at).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                            })
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>

      {/* ── Section 4: Quick links ── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          p: { md: 2.5, xs: 0 },
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} mb={2} p={1}>
          {isPlatformOwner ? "Quick navigate" : "Workspace actions"}
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(3, 1fr)",
            sm: "repeat(4, 1fr)",
            md: "repeat(7, 1fr)",
          }}
          gap={2}
        >
          {visibleQuickLinks.map((l) => (
            <Box
              key={l.label}
              onClick={() => navigate(l.path)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                cursor: "pointer",
                transition: "background .15s",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: l.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {React.cloneElement(l.icon, {
                  sx: { color: l.color, fontSize: 22 },
                })}
              </Box>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                textAlign="center"
              >
                {l.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>
    </Box>
  );
}
